/**
 * Values bound to <input type="datetime-local"> must use the form:
 *   YYYY-MM-DDTHH:mm
 * Safari rejects locale strings (e.g. "MM/DD/YYYY, hh:mm A").
 */

const DATETIME_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function formatFromLocalParts(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): string {
  return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}`;
}

function formatFromDate(d: Date): string {
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  return formatFromLocalParts(
    d.getFullYear(),
    d.getMonth() + 1,
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
  );
}

/**
 * Parse US-style datetime strings:
 * - "M/D/YYYY, h:mm AM"
 * - "M/D/YYYY h:mm:ss PM"
 * - "M/D/YYYY, HH:mm[:ss]" (24h)
 */
function tryParseUsDateTimeComma(s: string): Date | null {
  const trimmed = s.trim();
  const m = trimmed.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,\s*|\s+)(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?\s*$/i,
  );
  if (!m) {
    return null;
  }
  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  let hour = Number(m[4]);
  const minute = Number(m[5]);
  const second = Number(m[6] ?? 0);
  const ap = m[7]?.toUpperCase();

  if (ap) {
    if (ap === "PM" && hour !== 12) {
      hour += 12;
    }
    if (ap === "AM" && hour === 12) {
      hour = 0;
    }
  }

  const d = new Date(year, month - 1, day, hour, minute, second, 0);
  // Guard against overflow (e.g. 13/99/2026 55:99) being auto-normalized.
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day ||
    d.getHours() !== hour ||
    d.getMinutes() !== minute ||
    d.getSeconds() !== second
  ) {
    return null;
  }

  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Normalize any reasonable date/time input to datetime-local string, or "" if unknown.
 */
export function toDateTimeLocalString(
  value: string | Date | null | undefined,
): string {
  if (value == null || value === "") {
    return "";
  }
  if (value instanceof Date) {
    return formatFromDate(value);
  }

  const s = String(value).trim();
  if (!s) {
    return "";
  }

  if (DATETIME_LOCAL.test(s)) {
    return s;
  }

  // ISO-like: YYYY-MM-DDTHH:mm[:ss][.fff][Z|±offset]
  const isoLike = s.match(
    /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/i,
  );
  if (isoLike) {
    return `${isoLike[1]}T${isoLike[2]}`;
  }

  // Plain date + time without TZ (e.g. API "2026-04-16 12:30:00")
  const spaced = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (spaced) {
    return `${spaced[1]}T${spaced[2]}`;
  }

  const us = tryParseUsDateTimeComma(s);
  if (us) {
    return formatFromDate(us);
  }

  const d = new Date(s);
  return formatFromDate(d);
}

/**
 * Parse a datetime-local value as **local** wall time (no UTC shift).
 */
export function parseDateTimeLocalToDate(value: string): Date {
  const normalized = toDateTimeLocalString(value);
  if (!normalized) {
    return new Date(NaN);
  }
  const [datePart, timePart] = normalized.split("T");
  const [y, mo, da] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  return new Date(y, mo - 1, da, h, mi, 0, 0);
}

/**
 * From a `datetime-local` control value (`YYYY-MM-DDTHH:mm`): plain local
 * `YYYY-MM-DDTHH:mm:00.000` for APIs — no `Z`, no UTC conversion.
 */
export function dateTimeLocalToPlainIso(value: string): string {
  const n = toDateTimeLocalString(value);
  return n ? `${n}:00.000` : "";
}

/**
 * Local wall time as `YYYY-MM-DDTHH:mm:ss.sss` — no timezone suffix,
 * no `toISOString()` / UTC conversion.
 */
export function localDateTimeToPlainBackend(d: Date = new Date()): string {
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const base = toDateTimeLocalString(d);
  const sec = pad2(d.getSeconds());
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${base}:${sec}.${ms}`;
}

/** API compact dates: YYYYMMDD → MM/DD/YYYY (no timezone shift). */
export function formatYyyyMmDdToUs(value: string | number): string {
  const digits = String(value).replace(/\D/g, "");
  if (digits.length !== 8) {
    return "";
  }
  const year = digits.slice(0, 4);
  const month = digits.slice(4, 6);
  const day = digits.slice(6, 8);
  return `${month}/${day}/${year}`;
}

const US_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
};

function dateFromYmd(year: number, month: number, day: number): Date | null {
  const dt = new Date(year, month - 1, day);
  if (
    Number.isNaN(dt.getTime()) ||
    dt.getFullYear() !== year ||
    dt.getMonth() !== month - 1 ||
    dt.getDate() !== day
  ) {
    return null;
  }
  dt.setHours(0, 0, 0, 0);
  return dt;
}

/** Parse stored/API values to a local calendar Date. */
export function parseDateValue(
  value: string | number | Date | null | undefined,
): Date | null {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  // Typed UI dates: MM/DD/YYYY (must run before compact YYYYMMDD).
  // "04/22/1988" has 8 digits, but is not year 0422.
  const usSlash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usSlash) {
    return dateFromYmd(
      Number(usSlash[3]),
      Number(usSlash[1]),
      Number(usSlash[2]),
    );
  }

  const isoDateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateOnly) {
    return dateFromYmd(
      Number(isoDateOnly[1]),
      Number(isoDateOnly[2]),
      Number(isoDateOnly[3]),
    );
  }

  // API compact dates: YYYYMMDD with no separators.
  if (/^\d{8}$/.test(raw)) {
    return dateFromYmd(
      Number(raw.slice(0, 4)),
      Number(raw.slice(4, 6)),
      Number(raw.slice(6, 8)),
    );
  }

  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** US display MM/DD/YYYY for any supported API value. */
export function formatUsDate(
  value: string | number | Date | null | undefined,
): string {
  if (value == null || value === "") {
    return "";
  }

  const raw = String(value).trim();
  if (/^\d{8}$/.test(raw)) {
    return formatYyyyMmDdToUs(raw);
  }

  const dt = parseDateValue(value);
  if (!dt) {
    return "";
  }
  return dt.toLocaleDateString("en-US", US_DATE_OPTIONS);
}

/** US MM/DD/YYYY for a date plus N calendar days (e.g. eligibility + 120). */
export function formatUsDatePlusDays(
  value: string | number | Date | null | undefined,
  days: number,
): string {
  const start = parseDateValue(value);
  if (!start) {
    return "";
  }
  const result = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  result.setDate(result.getDate() + days);
  return formatUsDate(result);
}

/** US display with fallback (e.g. "Not Set"). */
export function formatUsDateDisplay(
  value: string | number | Date | null | undefined,
  fallback = "Not Set",
): string {
  if (value == null || String(value).trim() === "") {
    return fallback;
  }
  const formatted = formatUsDate(value);
  return formatted || fallback;
}

/** US display: h:mm AM/PM (12-hour, not 24-hour). */
export function formatUsTime(
  value: string | number | Date | null | undefined,
  fallback = "",
): string {
  if (value == null || value === "") {
    return fallback;
  }

  let date: Date | null =
    value instanceof Date ? value : parseDateValue(value);
  if (!date && typeof value === "string") {
    const d = new Date(value);
    date = Number.isNaN(d.getTime()) ? null : d;
  }
  if (!date || Number.isNaN(date.getTime())) {
    return fallback;
  }

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours || 12;
  return `${hours}:${pad2(minutes)} ${ampm}`;
}

/** US display MM/DD/YYYY, h:mm AM/PM */
export function formatUsDateTime(
  value: string | number | Date | null | undefined,
  fallback = "Not Set",
): string {
  if (value == null || String(value).trim() === "") {
    return fallback;
  }

  const raw = String(value).trim();
  const compact = raw.replace(/\D/g, "");
  if (compact.length === 8 && /^\d{8}$/.test(compact)) {
    return formatYyyyMmDdToUs(compact);
  }

  const dt = parseDateValue(value);
  if (!dt) {
    return String(value);
  }
  return dt.toLocaleString("en-US", {
    ...US_DATE_OPTIONS,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** Meridiem from a US datetime display string (MM/DD/YYYY h:mm AM/PM). */
export function getUsDateTimeDisplayMeridiem(value: string): "AM" | "PM" {
  const match = value.match(/\b(AM|PM)\b/i);
  return match?.[1]?.toUpperCase() === "PM" ? "PM" : "AM";
}

export function formatUsDateTimeDisplayFromParts(
  digits: string,
  meridiem: "AM" | "PM" = "AM",
): string {
  const clipped = digits.slice(0, 12);
  if (!clipped) {
    return "";
  }

  const monthPart = clipped.slice(0, 2).padEnd(2, "M");
  const dayPart = clipped.slice(2, 4).padEnd(2, "D");
  const yearPart = clipped.slice(4, 8).padEnd(4, "Y");
  const hourPart = clipped.slice(8, 10).padEnd(2, "-");
  const minutePart = clipped.slice(10, 12).padEnd(2, "-");
  return `${monthPart}/${dayPart}/${yearPart} ${hourPart}:${minutePart} ${meridiem}`;
}

export function formatUsDateTimeDisplayInput(value: string): string {
  const raw = String(value || "");
  const digits = raw.replace(/\D/g, "");
  return formatUsDateTimeDisplayFromParts(
    digits,
    getUsDateTimeDisplayMeridiem(raw),
  );
}

/** Parse MM/DD/YYYY h:mm AM/PM display → datetime-local value. */
export function tryNormalizeUsDateTimeDisplay(value: string): string {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  const m = raw.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,\s*|\s+)(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
  );
  if (!m) {
    return "";
  }

  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  let hour = Number(m[4]);
  const minute = Number(m[5]);
  const meridiem = m[6].toUpperCase();
  if (meridiem === "PM" && hour !== 12) {
    hour += 12;
  }
  if (meridiem === "AM" && hour === 12) {
    hour = 0;
  }

  const d = new Date(year, month - 1, day, hour, minute, 0, 0);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day ||
    d.getHours() !== hour ||
    d.getMinutes() !== minute
  ) {
    return "";
  }

  return toDateTimeLocalString(d);
}

export function formatUsDateTimeDisplayFromDateTimeLocal(
  value: string | Date | null | undefined,
): string {
  const normalized = toDateTimeLocalString(value);
  if (!normalized) {
    return "";
  }
  const d = parseDateTimeLocalToDate(normalized);
  if (Number.isNaN(d.getTime())) {
    return "";
  }

  const month = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const year = d.getFullYear();
  const hours = d.getHours() % 12 || 12;
  const minutes = pad2(d.getMinutes());
  const meridiem = d.getHours() >= 12 ? "PM" : "AM";
  return `${month}/${day}/${year} ${hours}:${minutes} ${meridiem}`;
}

/** Parse a complete US display string; do not re-pack digits (avoids 9:23 → 92:3). */
export function parseUsDateTimeDisplayInput(
  value: string,
): { normalized: string; display: string } | null {
  const normalized = tryNormalizeUsDateTimeDisplay(value);
  if (!normalized) {
    return null;
  }
  return {
    normalized,
    display: formatUsDateTimeDisplayFromDateTimeLocal(normalized),
  };
}

export function countDigitsBefore(value: string, position: number): number {
  return value.slice(0, Math.max(0, position)).replace(/\D/g, "").length;
}

export function getCaretPositionForDigitIndex(
  value: string,
  digitIndex: number,
): number {
  if (digitIndex <= 0) {
    return 0;
  }

  let seen = 0;
  for (let i = 0; i < value.length; i += 1) {
    if (/\d/.test(value[i])) {
      seen += 1;
    }
    if (seen >= digitIndex) {
      return i + 1;
    }
  }
  return value.length;
}

export function isAllowedUsDateTimeDisplayDigits(digits: string): boolean {
  const month = digits.slice(0, 2);
  const day = digits.slice(2, 4);
  const hour = digits.slice(8, 10);
  const minute = digits.slice(10, 12);

  if (month.length === 2) {
    const n = Number(month);
    if (n < 1 || n > 12) {
      return false;
    }
  }
  if (day.length === 2) {
    const n = Number(day);
    if (n < 1 || n > 31) {
      return false;
    }
  }
  if (hour.length === 2) {
    const n = Number(hour);
    if (n < 1 || n > 12) {
      return false;
    }
  }
  if (minute.length === 2 && Number(minute) > 59) {
    return false;
  }

  return true;
}

function toLocalDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = parseDateValue(value);
  if (parsed) {
    return parsed;
  }
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** US range: MM/DD/YYYY, h:mm AM – h:mm AM (same day) or full datetimes. */
export function formatUsDateTimeRange(
  startIso: string | Date | null | undefined,
  endIso?: string | Date | null | undefined,
  fallback = "-",
): string {
  const start = toLocalDate(startIso);
  if (!start) {
    return fallback;
  }

  if (endIso == null || String(endIso).trim() === "") {
    return `${formatUsDate(start)}, ${formatUsTime(start)}`;
  }

  const end = toLocalDate(endIso);
  if (!end) {
    return `${formatUsDate(start)}, ${formatUsTime(start)}`;
  }

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (sameDay) {
    return `${formatUsDate(start)}, ${formatUsTime(start)} – ${formatUsTime(end)}`;
  }

  return `${formatUsDateTime(start)}, ${formatUsDateTime(end)}`;
}

const CENTRAL_TIME_ZONE = "America/Chicago";

function centralDateTimeParts(d: Date): {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
} {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: CENTRAL_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);

  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";

  return {
    year: pick("year"),
    month: pick("month"),
    day: pick("day"),
    hour: pick("hour"),
    minute: pick("minute"),
    second: pick("second"),
  };
}

/** US Central wall time as `YYYY-MM-DDTHH:mm:ss.sss` (no Z suffix). */
export function centralDateTimeToPlainBackend(d: Date = new Date()): string {
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const p = centralDateTimeParts(d);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}.000`;
}

/**
 * Preserve API Central datetimes or convert legacy UTC ISO values to Central plain form.
 */
export function preserveOrConvertToCentralDateTime(
  value: string | null | undefined,
): string | null {
  if (value == null || value === "") {
    return null;
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  if (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw) &&
    !/[zZ]$/.test(raw) &&
    !/[+-]\d{2}:\d{2}$/.test(raw)
  ) {
    return raw;
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw;
  }

  return centralDateTimeToPlainBackend(d);
}

/** API date filter: local calendar YYYY-MM-DD (no timezone shift). */
export function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
